import { ChangeDetectorRef, Component, ViewContainerRef } from '@angular/core';
import { AngularComponentFactory } from 'survey-angular-ui';
import { M1SearchFormWidgetModel } from './m1-search-form.widget.model';
import { RendererFactory } from 'survey-core';
import { FormBuilder, Validators } from '@angular/forms';
import { NrdsMembersApiService, SnackbarService } from '@tangilla/tg-ng-core';
import { RequestParameters } from '@tangilla/tg-ng-models';
import { SurveyService } from '../../../../core/services/survey/survey.service';
import { M1SearchFormWidgetJson } from './m1-search-form.widget.json';
import { M1SearchWidgetService } from './services/m1-search.widget.service';
import { QuestionsService } from '../../../../core/services/survey/questions.service';
import { VariablesService } from '../../../../core/services/survey/variables.service';
import { FormWidgetUtilsService } from '../../../../core/services/utils/form-widget.utils.service';
import { TangillaQuestion } from '../tangilla-question';
import { ErrorsService } from '../../../../core/services/utils/errors.service';

@Component({
  selector: 'tgf-m1-search-form-widget',
  templateUrl: './m1-search-form.widget.component.html',
  styleUrls: ['./m1-search-form.widget.component.scss']
})
export class M1SearchFormWidgetComponent extends TangillaQuestion<M1SearchFormWidgetModel> {
  isLoading = false;
  isLoadingSearchM1Member = false;
  isValidM1Member = false;

  m1SearchWidgetJson = new M1SearchFormWidgetJson(this.surveyService);
  questionJson: any;

  constructor(
    private variablesService: VariablesService,
    private nrdsMembersApiService: NrdsMembersApiService,
    private m1SearchWidgetService: M1SearchWidgetService,
    private formWidgetUtilsService: FormWidgetUtilsService,
    private errorsService: ErrorsService,
    formBuilder: FormBuilder,
    surveyService: SurveyService,
    questionsService: QuestionsService,
    changeDetectorRef: ChangeDetectorRef,
    viewContainerRef?: ViewContainerRef
  ) {
    super(formBuilder, surveyService, questionsService, changeDetectorRef, viewContainerRef);
  }

  override ngOnInit() {
    super.ngOnInit();
    this.initForm();
  }

  initForm() {
    this.addM1SearchControls();
    this.mapQuestionJson();

    this.questionForm.get('member_nrds_id')?.valueChanges.subscribe(value => {
      if (value) {
        const stringValue = value.toString();
        if (stringValue.length < 9) {
          const padded = stringValue.padStart(9, '0');
          this.questionForm.get('member_nrds_id')?.setValue(padded, {emitEvent: false});
        }
      }
    });

    if (this.questionsService.nrdsSearch?.value !== 'NOTNAR' && (this.questionsService.nrds?.value || this.questionsService.licenseNumber?.value)) {
      this.searchM1Member(true);
    }
    this.questionForm.valueChanges.subscribe(data => {
      this.changeDetectorRef.detectChanges();
    });
  }

  mapQuestionJson() {
    const questionJson = this.m1SearchWidgetJson.getWidgetJson(this.questionForm!, this.actualModel);
    questionJson.valueChangesFunctions = {};
    questionJson.valueChangesFunctions.nrds_search = (nrdsSearch: string) => {
      this.resetNrdsSearchValues();

      if (nrdsSearch === 'NOTNAR') {
        this.actualModel.value = true;
      } else {
        this.actualModel.value = undefined;
      }
      this.changeDetectorRef.detectChanges();
    };
    questionJson.valueChangesFunctions.nrds_member = (nrdsMember: string) => {
      if (nrdsMember) {
        this.actualModel.value = true;
      } else {
        this.actualModel.value = undefined;
      }
    };
    this.questionJson = questionJson;
  }

  resetNrdsSearchValues() {
    this.formWidgetUtilsService.updateControlValue(this.questionForm.get('member_nrds_id'), null);
    this.formWidgetUtilsService.updateControlValue(this.questionForm.get('state'), null);
    this.formWidgetUtilsService.updateControlValue(this.questionForm.get('nrds_member'), null);
    this.isValidM1Member = false;
    const nrdsQuestion = this.questionJson
      .questions?.find((question: any) => question.name === 'NRDS');
    if (nrdsQuestion) {
      nrdsQuestion.choices = [];
    }
  }

  addM1SearchControls() {
    const nrdsValue = this.questionsService.nrds?.value;
    const licenseNumberValue = this.questionsService.licenseNumber?.value;

    this.addFormControl('nrds_search', 'NRDSSearch', [Validators.required], nrdsValue ? 'NRDS' : licenseNumberValue ? 'LIC' : null);
    this.addFormControl('member_nrds_id', 'NRDSnum', [], nrdsValue ? nrdsValue : null);
    this.addFormControl('state', 'NRDSstate');
    this.addFormControl('nrds_member', 'NRDS', []);
  }

  searchM1Member(isInitializing?: boolean) {
    const that = this;
    this.submitForm(() => that.doSearchM1Member(isInitializing));
  }

  doSearchM1Member(isInitializing?: boolean) {
    this.isLoadingSearchM1Member = true;
    this.actualModel.clearErrors();
    this.actualModel.value = undefined;
    this.changeDetectorRef.detectChanges();
    const queryParams = this.getSearchQueryParams();

    const parameters = (new RequestParameters(null)).setAdditionalFields(queryParams);

    const nrdsQuestion = this.questionJson
      .questions?.find((question: any) => question.name === 'NRDS');
    if (nrdsQuestion) {
      nrdsQuestion.choices = [];
    }
    this.nrdsMembersApiService.getAll(parameters)
      .subscribe({
        next: async data => {
          await this.m1SearchWidgetService.processM1Member(data, this.questionForm.get('nrds_search')?.value, this.questionJson, this.actualModel);
          this.isValidM1Member = nrdsQuestion.choices?.length > 0;
          this.isLoadingSearchM1Member = false;
          if (isInitializing && this.questionsService.nrds?.value) {
            this.actualModel.value = true;
          }
          this.changeDetectorRef.detectChanges();
        },
        error: error => {
          this.actualModel.addError('This NRDS Member is not valid');
          this.isValidM1Member = false;
          this.isLoadingSearchM1Member = false;

          this.errorsService.processError(error, 'soft');
          this.changeDetectorRef.detectChanges();
        }
      });
  }

  private getSearchQueryParams() {
    const formValue = this.questionForm.getRawValue();

    let queryParams: any = {};
    switch (formValue.nrds_search) {
      case 'NRDS':
        queryParams.member_nrds_id = formValue.member_nrds_id;
        break;
      case 'LIC':
        queryParams.real_estate_license = this.questionsService.licenseNumber?.value;
        queryParams.state = this.variablesService.state;
        break;
      case 'NAME':
        queryParams.last_name = this.questionsService.lastName?.value;
        queryParams.first_name = this.questionsService.firstName?.value;
        queryParams.state = this.variablesService.state;
        break;
      case 'NAMESTATE':
        queryParams.last_name = this.questionsService.lastName?.value;
        queryParams.first_name = this.questionsService.firstName?.value;
        queryParams.state = formValue.state;
        break;
    }

    return queryParams;
  }

  isItLoading() {
    return this.isLoading || this.isLoadingSearchM1Member;
  }
}

RendererFactory.Instance.registerRenderer(
  'm1_search_form',
  'm1_search_form',
  'tg_m1_search_form',
  true
);
AngularComponentFactory.Instance.registerComponent('tg_m1_search_form', M1SearchFormWidgetComponent);

